import type { DashboardData, NotaSimplificada, Divergencia, Severity, StatusSummary } from "./fiscal-types";

export function consolidate(notas: NotaSimplificada[], divergencias: Divergencia[]): DashboardData {
  // 1. First Pass: Detect the most frequent CNPJ (Main Company)
  const cnpjCounts = new Map<string, { nome: string; count: number }>();
  for (const n of notas) {
    if (n.cnpjEmit) {
      const cur = cnpjCounts.get(n.cnpjEmit) ?? { nome: n.emitente, count: 0 };
      cur.count++; cnpjCounts.set(n.cnpjEmit, cur);
    }
    if (n.cnpjDest) {
      const cur = cnpjCounts.get(n.cnpjDest) ?? { nome: n.destinatario, count: 0 };
      cur.count++; cnpjCounts.set(n.cnpjDest, cur);
    }
  }

  let topCnpj = "", topNome = "", maxCount = -1;
  cnpjCounts.forEach((v, k) => { if (v.count > maxCount) { maxCount = v.count; topCnpj = k; topNome = v.nome; } });

  // 2. Second Pass: Consolidation with Smart Heuristics
  const stats = {
    totalNotas: notas.length,
    modelo55: notas.filter(n => n.modelo === 55).length,
    modelo65: notas.filter(n => n.modelo === 65).length,
    totalRevenue: 0,
    periodo: { inicio: "", fim: "" }
  };

  let minDate = Infinity;
  let maxDate = -Infinity;

  const riskSummary = { alta: 0, media: 0, baixa: 0 };
  const statusSummary: StatusSummary = {
    autorizadas: { quantidade: 0, valorTotal: 0 },
    contingencia: { quantidade: 0, valorTotal: 0 },
    inutilizadas: { quantidade: 0, valorTotal: 0 },
    canceladas: { quantidade: 0, valorTotal: 0 },
  };

  const taxMapE = new Map<string, { totalValue: number; totalBase: number }>();
  const taxMapS = new Map<string, { totalValue: number; totalBase: number }>();
  
  const operacoesSummary = {
    entradas: { quantidade: 0, valorTotal: 0 },
    saidas: { quantidade: 0, valorTotal: 0 },
    indefinidas: { quantidade: 0 },
  };

  const diasMap = new Map<string, { date: string; Entrada: number; Saida: number; Canceladas: number }>();
  const cfopMapE = new Map<string, { cfop: string; vConsolidado: number; icms: number; fcp: number; icmsST: number; fcpST: number; }>();
  const cfopMapS = new Map<string, { cfop: string; vConsolidado: number; icms: number; fcp: number; icmsST: number; fcpST: number; }>();
  
  // Intelligence Maps
  const fornecedoresMap = new Map<string, { nome: string; cnpj: string; valor: number }>();
  const fornecedoresSNMap = new Map<string, { nome: string; cnpj: string; valor: number }>();
  const clientesMap = new Map<string, { nome: string; cnpj: string; valor: number }>();
  const produtosMap = new Map<string, { nome: string; quantidade: number; valorTotal: number }>();
  const ufMap = new Map<string, { uf: string; valor: number }>();
  const pagamentosMap = new Map<string, { label: string; valor: number }>();
  const associacoesMap = new Map<string, { p1: string; p2: string; count: number }>();
  const precosPorProd = new Map<string, number[]>();
  const comprasPorCliente = new Map<string, number[]>();
  
  let nfeCompra = 0, nfeVenda = 0, nfceCompra = 0, nfceVenda = 0;
  let totalItens = 0, ncmGenerico = 0, eanInvalido = 0, semCest = 0;

  for (const n of notas) {
    const isValid = n.notaStatus === "AUTORIZADA" || n.notaStatus === "CONTINGENCIA";
    const isCancelada = n.notaStatus === "CANCELADA" || n.notaStatus === "INUTILIZADA";
    
    // Heuristic for Operation Type if INDEFINIDA
    let tipoOp = n.tipoOperacao;
    if (tipoOp === "INDEFINIDA" && topCnpj) {
      if (n.cnpjEmit === topCnpj) tipoOp = "SAIDA";
      else if (n.cnpjDest === topCnpj) tipoOp = "ENTRADA";
      else if (n.modelo === 65) tipoOp = "SAIDA"; // NFC-e is almost always Sale
    }

    const isEntrada = tipoOp === "ENTRADA";
    const isSaida = tipoOp === "SAIDA";

    // Timeline
    if (n.emissao) {
      const d = n.emissao.split("T")[0];
      const time = new Date(d).getTime();
      if (!isNaN(time)) {
        if (time < minDate) minDate = time;
        if (time > maxDate) maxDate = time;
      }

      const dayKey = d.slice(-2);
      if (!diasMap.has(dayKey)) diasMap.set(dayKey, { date: dayKey, Entrada: 0, Saida: 0, Canceladas: 0 });
      const row = diasMap.get(dayKey)!;
      if (isCancelada) row.Canceladas++;
      else if (isEntrada) row.Entrada++;
      else if (isSaida) row.Saida++;
    }

    if (isValid) {
      stats.totalRevenue += n.valorOficial;
      
      const st = n.notaStatus.toLowerCase() as keyof StatusSummary;
      if (statusSummary[st]) {
        statusSummary[st].quantidade++;
        statusSummary[st].valorTotal += n.valorOficial;
      }

      // Balanço
      if (isEntrada) {
        if (n.modelo === 55) nfeCompra += n.valorOficial;
        else nfceCompra += n.valorOficial;
      } else if (isSaida) {
        if (n.modelo === 55) nfeVenda += n.valorOficial;
        else nfceVenda += n.valorOficial;
      }

      // Impostos
      const targetTaxMap = isEntrada ? taxMapE : taxMapS;
      for (const imp of n.impostosDetalhe ?? []) {
        if (!targetTaxMap.has(imp.sigla)) targetTaxMap.set(imp.sigla, { totalValue: 0, totalBase: 0 });
        const cur = targetTaxMap.get(imp.sigla)!;
        cur.totalValue += imp.valor;
        cur.totalBase += imp.base;
      }

      // Fornecedores / Clientes
      if (isEntrada && n.cnpjEmit) {
        if (!fornecedoresMap.has(n.cnpjEmit)) fornecedoresMap.set(n.cnpjEmit, { nome: n.emitente, cnpj: n.cnpjEmit, valor: 0 });
        fornecedoresMap.get(n.cnpjEmit)!.valor += n.valorOficial;
        
        if (n.detalhes?.emitente?.CRT === "1") {
          if (!fornecedoresSNMap.has(n.cnpjEmit)) fornecedoresSNMap.set(n.cnpjEmit, { nome: n.emitente, cnpj: n.cnpjEmit, valor: 0 });
          fornecedoresSNMap.get(n.cnpjEmit)!.valor += n.valorOficial;
        }

        const uf = n.ufEmit || "ND";
        if (!ufMap.has(uf)) ufMap.set(uf, { uf, valor: 0 });
        ufMap.get(uf)!.valor += n.valorOficial;
      }

      if (isSaida && n.cnpjDest) {
        if (!clientesMap.has(n.cnpjDest)) clientesMap.set(n.cnpjDest, { nome: n.destinatario, cnpj: n.cnpjDest, valor: 0 });
        clientesMap.get(n.cnpjDest)!.valor += n.valorOficial;

        const date = new Date(n.emissao).getTime();
        if (!isNaN(date)) {
          if (!comprasPorCliente.has(n.cnpjDest)) comprasPorCliente.set(n.cnpjDest, []);
          comprasPorCliente.get(n.cnpjDest)!.push(date);
        }
      }

      // CFOP & Itens & Intelligence Hub
      const targetCfopMap = isEntrada ? cfopMapE : cfopMapS;
      if (n.itens) {
        const itensNaNota = new Set<string>();
        for (const item of n.itens) {
          totalItens++;
          const c = item.cfop || n.cfopPrincipal || "Outros";
          if (!targetCfopMap.has(c)) targetCfopMap.set(c, { cfop: c, vConsolidado: 0, icms: 0, fcp: 0, icmsST: 0, fcpST: 0 });
          const cRow = targetCfopMap.get(c)!;
          cRow.vConsolidado += item.vProd;
          cRow.icms += (item.impostos?.icms?.vICMS || 0);
          cRow.fcp += (item.impostos?.fcp?.vFCP || 0);
          cRow.icmsST += (item.impostos?.icmsST?.vICMSST || 0);
          cRow.fcpST += (item.impostos?.fcpST?.vFCPST || 0);

          const ncm = (item.ncm || "").replace(/\D/g, "");
          if (ncm === "00000000" || ncm === "99999999" || ncm === "") ncmGenerico++;
          const ean = (item.produtoExtra?.cEAN || "").toUpperCase();
          if (!ean || ean === "SEM GTIN" || ean === "0") eanInvalido++;
          if ((c.startsWith("54") || c.startsWith("14")) && !item.produtoExtra?.CEST) semCest++;

          // Intelligence: Top Produtos & Preços
          if (isSaida) {
            const nomeProd = (item.xProd || "Desconhecido").toUpperCase();
            itensNaNota.add(item.xProd);
            if (!produtosMap.has(item.xProd)) produtosMap.set(item.xProd, { nome: item.xProd, quantidade: 0, valorTotal: 0 });
            const p = produtosMap.get(item.xProd)!;
            p.quantidade += item.qCom;
            p.valorTotal += item.vProd;

            if (item.vUnCom > 0) {
              if (!precosPorProd.has(nomeProd)) precosPorProd.set(nomeProd, []);
              precosPorProd.get(nomeProd)!.push(item.vUnCom);
            }
          }
        }

        // Intelligence: Market Basket (Top Associações)
        if (isSaida) {
          const arrItens = Array.from(itensNaNota);
          for (let i = 0; i < arrItens.length; i++) {
            for (let j = i + 1; j < arrItens.length; j++) {
              const pair = [arrItens[i], arrItens[j]].sort().join(" | ");
              if (!associacoesMap.has(pair)) associacoesMap.set(pair, { p1: arrItens[i], p2: arrItens[j], count: 0 });
              associacoesMap.get(pair)!.count++;
            }
          }
        }
      }

      // Pagamentos
      if (isSaida && n.detalhes?.pagamento?.detPag) {
        const detPag = Array.isArray(n.detalhes.pagamento.detPag) ? n.detalhes.pagamento.detPag : [n.detalhes.pagamento.detPag];
        for (const pag of detPag) {
          const labelMap: Record<string, string> = { "01": "Dinheiro", "02": "Cheque", "03": "Cartão de Crédito", "04": "Cartão de Débito", "17": "PIX", "15": "Boleto", "99": "Outros" };
          const label = labelMap[pag.tPag] || "Outros";
          if (!pagamentosMap.has(label)) pagamentosMap.set(label, { label, valor: 0 });
          pagamentosMap.get(label)!.valor += (parseFloat(pag.vPag) || 0);
        }
      }

      // Operações Summary
      if (isEntrada) { operacoesSummary.entradas.quantidade++; operacoesSummary.entradas.valorTotal += n.valorOficial; }
      else if (isSaida) { operacoesSummary.saidas.quantidade++; operacoesSummary.saidas.valorTotal += n.valorOficial; }
      else { operacoesSummary.indefinidas.quantidade++; }
    } else {
      const st = n.notaStatus.toLowerCase() as keyof StatusSummary;
      if (statusSummary[st]) { statusSummary[st].quantidade++; statusSummary[st].valorTotal += n.valorOficial; }
    }

    if (n.divergencia === "ALTA") riskSummary.alta++;
    else if (n.divergencia === "MEDIA") riskSummary.media++;
    else if (n.divergencia === "BAIXA") riskSummary.baixa++;
  }

  // Final Calculations: Peak and Throughput
  const dayCounts = Array.from(diasMap.values()).map(d => d.Entrada + d.Saida + d.Canceladas);
  const peakProcessed = dayCounts.length > 0 ? Math.max(...dayCounts) : 0;
  const avgThroughput = dayCounts.length > 0 ? dayCounts.reduce((a, b) => a + b, 0) / dayCounts.length : 0;

  // Final Calculations: Trends (Comparing first half vs second half of period)
  const sortedTimeline = Array.from(diasMap.values()).sort((a, b) => a.date.localeCompare(b.date));
  const mid = Math.floor(sortedTimeline.length / 2);
  const firstHalf = sortedTimeline.slice(0, mid).reduce((acc, d) => acc + d.Entrada + d.Saida, 0);
  const secondHalf = sortedTimeline.slice(mid).reduce((acc, d) => acc + d.Entrada + d.Saida, 0);
  const volumeTrend = firstHalf > 0 ? ((secondHalf - firstHalf) / firstHalf) * 100 : 0;

  // Revenue Trend
  const revFirstHalf = notas.slice(0, Math.floor(notas.length / 2)).reduce((acc, n) => acc + n.valorOficial, 0);
  const revSecondHalf = notas.slice(Math.floor(notas.length / 2)).reduce((acc, n) => acc + n.valorOficial, 0);
  const revenueTrend = revFirstHalf > 0 ? ((revSecondHalf - revFirstHalf) / revFirstHalf) * 100 : 0;

  // Final Calculations: Outliers (Threshold loosened to 3 samples)
  const outliersPreco: any[] = [];
  precosPorProd.forEach((precos, nome) => {
    if (precos.length < 3) return;
    const media = precos.reduce((a, b) => a + b, 0) / precos.length;
    const variancia = precos.reduce((a, b) => a + Math.pow(b - media, 2), 0) / precos.length;
    const desvioPadrao = Math.sqrt(variancia);
    if (desvioPadrao > 0) {
      precos.forEach(p => {
        const zScore = Math.abs((p - media) / desvioPadrao);
        if (zScore > 2.0) outliersPreco.push({ produto: nome, preco: p, media, desvio: zScore.toFixed(2), impacto: Math.abs(p - media) });
      });
    }
  });

  // Final Calculations: Churn/Risco (Threshold loosened for smaller datasets)
  const hoje = Date.now();
  const clientesRisco: any[] = [];
  comprasPorCliente.forEach((dates, cnpj) => {
    if (dates.length < 2) return;
    dates.sort((a, b) => b - a);
    const diasInativo = (hoje - dates[0]) / (1000 * 60 * 60 * 24);
    const intervalos = [];
    for (let i = 0; i < dates.length - 1; i++) intervalos.push((dates[i] - dates[i+1]) / (1000 * 60 * 60 * 24));
    const mediaIntervalo = intervalos.reduce((a, b) => a + b, 0) / intervalos.length;
    // Lowered check to 1.2x media and 7 days
    if (diasInativo > mediaIntervalo * 1.2 && diasInativo > 7) {
      clientesRisco.push({ cnpj, nome: clientesMap.get(cnpj)?.nome || "Cliente", inatividade: Math.floor(diasInativo), frequenciaMedia: Math.floor(mediaIntervalo), status: diasInativo > mediaIntervalo * 2 ? "CRÍTICO" : "ALERTA" });
    }
  });

  if (minDate !== Infinity && maxDate !== -Infinity) {
    stats.periodo = {
      inicio: new Date(minDate).toISOString().split("T")[0],
      fim: new Date(maxDate).toISOString().split("T")[0]
    };
  }

  return {
    stats: {
      ...stats,
      peakProcessed,
      avgThroughput,
      volumeTrend,
      revenueTrend
    },
    taxCards: Array.from(taxMapS.values()).map((v, i) => ({ title: Array.from(taxMapS.keys())[i], ...v })),
    taxCardsE: Array.from(taxMapE.entries()).map(([title, data]) => ({ title, ...data })),
    taxCardsS: Array.from(taxMapS.entries()).map(([title, data]) => ({ title, ...data })),
    riskSummary, statusSummary,
    screensOrder: ["dashboard", "notas", "tributacao", "classificacao", "inspecao"],
    cnpjEmpresa: topCnpj, nomeEmpresa: topNome, operacoesSummary,
    chartTimeline: sortedTimeline,
    cfopDataEntrada: Array.from(cfopMapE.values()).sort((a, b) => a.cfop.localeCompare(b.cfop)),
    cfopDataSaida: Array.from(cfopMapS.values()).sort((a, b) => a.cfop.localeCompare(b.cfop)),
    saneamento: { 
      totalItens, 
      ncmGenerico, 
      eanInvalido, 
      semCest, 
      score: totalItens > 0 
        ? Math.max(0, 100 - (((ncmGenerico * 0.5) + (eanInvalido * 0.2)) / totalItens * 100)) 
        : 100 
    },
    intelligence: {
      topFornecedores: Array.from(fornecedoresMap.values()).sort((a, b) => b.valor - a.valor).slice(0, 50),
      topFornecedoresSN: Array.from(fornecedoresSNMap.values()).sort((a, b) => b.valor - a.valor).slice(0, 50),
      topClientes: Array.from(clientesMap.values()).sort((a, b) => b.valor - a.valor).slice(0, 50),
      topProdutos: Array.from(produtosMap.values()).sort((a, b) => b.valorTotal - a.valorTotal).slice(0, 100),
      topPagamentos: Array.from(pagamentosMap.values()).sort((a, b) => b.valor - a.valor),
      topAssociacoes: Array.from(associacoesMap.values()).sort((a, b) => b.count - a.count).slice(0, 12),
      outliersPreco: outliersPreco.sort((a, b) => b.impacto - a.impacto).slice(0, 10),
      clientesRisco: clientesRisco.sort((a, b) => b.inatividade - a.inatividade).slice(0, 10),
      relacaoCompraVenda: [ { name: "NF-e", Compra: nfeCompra, Venda: nfeVenda }, { name: "NFC-e", Compra: nfceCompra, Venda: nfceVenda } ],
      balancoGeral: { totalCompras: nfeCompra + nfceCompra, totalVendas: nfeVenda + nfceVenda, resultado: (nfeVenda + nfceVenda) - (nfeCompra + nfceCompra) },
      comprasPorUF: Array.from(ufMap.values()).sort((a, b) => b.valor - a.valor).slice(0, 10)
    }
  };
}
